import { useQuery } from "@tanstack/react-query";
import { fetchQuizruns, fetchQuizrun } from "@/api/events";
import type { Quizrun } from "@/types/domain";
import { quizrunKeys } from "@/api/queryKeys";

/**
 * Hook to fetch all quizruns from the database.
 *
 * staleTime keeps the cached list fresh across navigation so visiting an event
 * page (which reads this list for prev/next nav) doesn't trigger a cold refetch
 * of 3 selects on every mount. Admin mutations invalidate ["quizruns"], so
 * edits still refresh immediately.
 */
export const useQuizruns = () => {
  return useQuery({
    queryKey: quizrunKeys.list,
    queryFn: fetchQuizruns,
    staleTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to fetch a single quizrun by ID.
 */
export const useQuizrun = (quizrunId: number | null) => {
  return useQuery({
    queryKey: quizrunKeys.detail(quizrunId),
    queryFn: async (): Promise<Quizrun | null> => {
      if (quizrunId === null) return null;
      return fetchQuizrun(quizrunId);
    },
    enabled: quizrunId !== null,
  });
};
